import { Fragment, memo, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { getQuoteProductFooterFields } from '@views/screens/Quote/components/CreateGetQuote/Tabs/ProductDetails/utils';
import { Col, Divider, Form, Row } from 'antd';

import { ENV_VARS } from '@/env';
import { useRegisterForm } from '@/modules/validators';

import { routeFileSelector } from '../../../../../../../utilz/i18n';
import VerticalLine from '../../../../../../components/Bars/VerticalLine';
import Button from '../../../../../../components/Buttons/Button';
import PrimaryLabel from '../../../../../../components/Labels/PrimaryLabel';
import Paper from '../../../../../../components/Paper/Paper';
import FooterContentCard from '../../../../../common/FooterContentCard';
import HallProductDetailsCard from './HallProductDetailsCard';

const isHalliburton = ENV_VARS.REACT_APP_THEME === 'halliburton';
const buttonClassName = isHalliburton ? 'hb-button' : '';

const HallProductDetails = () => {
  const { user } = useSelector((state) => state.auth);
  const [form] = Form.useForm();
  useRegisterForm(form, 'productForm');
  const formItems = Form.useWatch('quoteDetails', form) || [];
  const { pathname: activeTab } = useLocation();
  const namespace = useMemo(() => routeFileSelector(activeTab), [activeTab]);
  const { t } = useTranslation([namespace, 'translation']);
  const [footerFields, setFooterFields] = useState([]);
  const location = useLocation();
  const prepopulatedData = location.state?.quote;

  const { data = {} } = useSelector((state) => state.shipment.shipmentDetails);
  const copyQuoteKey = useSelector((state) => state.common.copyQuoteKey);

  // biome-ignore lint/correctness/useExhaustiveDependencies: <skip redundant dependency>
  useEffect(() => {
    if (copyQuoteKey) {
      form.setFields([
        {
          name: 'quoteDetails',
          value: data?.quoteDetails?.map((item) => ({
            ...item,
            editMode: true,
          })),
        },
      ]);
    }
  }, [copyQuoteKey, data?.quoteDetails]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: <skip redundant dependency>
  useEffect(() => {
    setFooterFields(getQuoteProductFooterFields(t, formItems));
  }, [formItems]);

  useEffect(() => {
    if (prepopulatedData) {
      form.setFieldsValue({
        quoteDetails: [
          {
            cube: prepopulatedData.cube,
            dimUom: prepopulatedData.dimUOM,
            editMode: true,
            height: prepopulatedData.height,
            length: prepopulatedData.length,
            quantity: null,
            weight: prepopulatedData.weight,
            weightUom: prepopulatedData.weightUOM,
            width: prepopulatedData.width,
          },
        ],
      });
    }
  }, [form, prepopulatedData]);

  const isDisabledCopyProductDetailsButton = (items) => {
    return items.length === 0;
  };

  const handleDataChange = (data, index) => {
    const quoteDetailsLocal = form.getFieldValue('quoteDetails');
    quoteDetailsLocal[index] = { ...quoteDetailsLocal[index], ...data };
    form.setFieldValue('quoteDetails', quoteDetailsLocal);
  };

  const handleCopyLastProductDetails = () => {
    const quoteDetailsLocal = form.getFieldValue('quoteDetails');
    const lastProductDetailsEntry = quoteDetailsLocal[quoteDetailsLocal.length - 1];
    form.setFieldValue('quoteDetails', [...quoteDetailsLocal, lastProductDetailsEntry]);
  };

  return (
    <Form
      name="productForm"
      form={form}
      initialValues={{
        quoteDetails: [
          {
            billQtyUom: 'PT',
            createdBy: user?.data?.id,
            cube: null,
            dimUom: 'N',
            editMode: true,
            gridOperation: 1,
            height: null,
            isNew: true,
            itemSequenceKey: 1,
            length: null,
            quantity: null,
            weight: null,
            weightUom: 'LB',
            weightUomName: 'Pound',
            width: null,
          },
        ],
      }}
    >
      <Form.List
        name="quoteDetails"
        rules={[
          {
            validator: async (_, value) => {
              if (!value || value.length < 1) {
                return Promise.reject(new Error('At least one product must be added.'));
              }
            },
          },
        ]}
      >
        {(items, { add, remove }, { errors }) => (
          <>
            <Row justify="space-between" align="middle">
              <Col>
                <PrimaryLabel>{t('translation:getQuote.productDetails.productInformation')}</PrimaryLabel>
              </Col>
              <Col>
                <Row gutter={15}>
                  <Col>
                    <Button
                      dataTestId="product-details-add-product-button"
                      onClick={() => {
                        add({
                          billQtyUom: 'PT',
                          createdBy: user?.data?.id,
                          dimUom: 'N',
                          editMode: true,
                          gridOperation: 1,
                          height: null,
                          isNew: true,
                          itemSequenceKey: 1,
                          length: null,
                          quantity: null,
                          weight: null,
                          weightUom: 'LB',
                          width: null,
                        });
                      }}
                      className={buttonClassName}
                    >
                      {t('translation:default.buttons.addProduct')}
                    </Button>
                  </Col>
                  <Col>
                    <Button
                      dataTestId="product-details-copy-last-button"
                      disabled={isDisabledCopyProductDetailsButton(items)}
                      onClick={handleCopyLastProductDetails}
                      className={buttonClassName}
                    >
                      {t('translation:shipment.copyLastProductDetailsEntry')}
                    </Button>
                  </Col>
                </Row>
              </Col>
            </Row>
            <Divider className="border-top-border my-15" style={{ marginLeft: '-10%', width: '120%' }} />
            <div>
              {items.map(({ name }, index) => (
                <HallProductDetailsCard
                  key={name}
                  name={name}
                  index={index}
                  remove={remove}
                  itemDetails={formItems?.[name]}
                  handleDataChange={handleDataChange}
                  isLast={index + 1 === items.length}
                  dataTestId={`product-details-card-${index}`}
                />
              ))}
              <Form.ErrorList errors={errors} />
            </div>

            <div className="mt-15">
              <Paper className={isHalliburton ? 'hb-bg-color px-16 py-5' : 'background-header px-16 py-5'}>
                <Row justify="space-around">
                  {footerFields.map((field, index) => (
                    <Fragment key={field.label}>
                      <Col>
                        <FooterContentCard
                          reverse
                          align="middle"
                          defaultValue="0"
                          count={field.value}
                          label={field.label}
                          contentClass="text-white f-16 fw-600"
                          labelClass="text-light f-14"
                          dataTestId="footer-content-card"
                        />
                      </Col>
                      {index !== footerFields.length - 1 ? (
                        <Col>
                          <VerticalLine />
                        </Col>
                      ) : null}
                    </Fragment>
                  ))}
                </Row>
              </Paper>
            </div>
          </>
        )}
      </Form.List>
    </Form>
  );
};

export default memo(HallProductDetails);
